import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { fetchQuotaSummary } from '@/services/quotaService';
import { ADMIN_SECTIONS } from '@/config/adminNav';
import type { AdminSection, AdminToolItem } from '@/config/adminNav';
import { 
  Search, 
  ExternalLink, 
  LogOut, 
  ChevronDown, 
  Check, 
  X,
  Lock,
  ArrowRight
} from 'lucide-react';

export { ADMIN_SECTIONS, AdminSection, AdminToolItem };

export function AdminPortalNav() {
  const { user, role, isSuperAdmin, signOut } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quotaStatus, setQuotaStatus] = useState<'healthy' | 'warning' | 'critical'>('healthy');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const token = user ? await user.getIdToken() : null;
        const summary = await fetchQuotaSummary(token);
        if (isMounted && summary) {
          setQuotaStatus(summary.overallStatus);
        }
      } catch (e) {}
    })();

    const handleSync = (e: Event) => {
      const customEvent = e as CustomEvent<any>;
      if (customEvent.detail && isMounted) {
        setQuotaStatus(customEvent.detail.overallStatus || 'healthy');
      }
    };
    window.addEventListener('five-lens-quota-synced', handleSync);
    return () => {
      isMounted = false;
      window.removeEventListener('five-lens-quota-synced', handleSync);
    };
  }, [user]);

  // Determine active section based on current URL path
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const fullPath = currentPath + currentSearch;
  const isOverviewOrQuotas = currentPath === '/admin' || currentPath === '/admin/quotas';

  const getActiveSectionId = (): AdminSection['id'] => {
    if (currentPath === '/admin' || currentPath === '/admin/') {
      return 'overview';
    }
    if (
      currentPath.startsWith('/admin/blog') ||
      currentPath.startsWith('/admin/testimonials') ||
      currentPath.startsWith('/admin/trust-logos') ||
      currentPath.startsWith('/admin/regional-content')
    ) {
      return 'content';
    }
    if (
      currentPath.startsWith('/admin/practitioners') ||
      currentPath.startsWith('/admin/calendar') ||
      currentPath.startsWith('/admin/commissions')
    ) {
      return 'network';
    }
    return 'system';
  };

  const activeSectionId = getActiveSectionId();
  const activeSection = ADMIN_SECTIONS.find(s => s.id === activeSectionId) || ADMIN_SECTIONS[0];

  // Helper to test if a tool link is active
  const isToolActive = (tool: AdminToolItem) => {
    if (tool.path.includes('?')) {
      const [toolPath, toolQuery] = tool.path.split('?');
      if (currentPath === toolPath && currentSearch.includes(toolQuery)) {
        return true;
      }
      return false;
    }
    return currentPath === tool.path;
  };

  // Keyboard shortcut (Cmd+K or Ctrl+K) to open quick switcher
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      } else if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
    }
  }, [searchOpen]);

  // All tools for quick search
  const allTools = ADMIN_SECTIONS.flatMap(section => 
    section.tools.map(tool => ({ ...tool, sectionLabel: section.label }))
  );

  const filteredTools = searchQuery.trim()
    ? allTools.filter(t => 
        t.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.sectionLabel.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allTools;

  return (
    <div className="bg-ink text-white border-b-2 border-rule-3 shadow-md sticky top-0 z-40">
      {/* Top Bar: Brand, Primary Section Tabs, Search, User Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 gap-4">
          
          {/* Brand & Section Tabs */}
          <div className="flex items-center gap-1 sm:gap-4 overflow-x-auto no-scrollbar py-1">
            <Link 
              to="/admin" 
              className="flex items-center gap-2 font-serif font-bold text-white text-base tracking-tight shrink-0 mr-2 hover:opacity-90 transition-opacity"
            >
              <div className="w-7 h-7 bg-rule-3 text-white rounded flex items-center justify-center font-sans text-xs font-black">
                FL
              </div>
              <span className="hidden sm:inline">Portal</span>
            </Link>

            <span className="h-5 w-px bg-white/20 hidden sm:block shrink-0" />

            {/* Section Tabs */}
            <nav className="flex items-center gap-1 shrink-0" aria-label="Admin Sections">
              {ADMIN_SECTIONS.map(section => {
                const Icon = section.icon;
                const isActive = activeSectionId === section.id;
                const isSystem = section.id === 'system';
                const showDot = isSystem && !isOverviewOrQuotas && quotaStatus !== 'healthy';

                return (
                  <Link
                    key={section.id}
                    to={section.defaultPath}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all whitespace-nowrap relative ${
                      isActive
                        ? 'bg-rule-3 text-white shadow-sm'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{section.label}</span>
                    {showDot && (
                      <span className={`w-2 h-2 rounded-full absolute -top-0.5 -right-0.5 ${
                        quotaStatus === 'critical' ? 'bg-rose-500 animate-pulse' : 'bg-amber-400'
                      }`} title={quotaStatus === 'critical' ? 'Critical quota alert' : 'Quota warning'} />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right Actions: Quick Search, Role Badge, View Site, Logout */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Quick Tool Switcher Trigger */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-2.5 py-1 text-xs text-white/70 bg-white/10 hover:bg-white/20 border border-white/15 rounded-md transition-colors"
              title="Jump to any admin tool (Cmd+K)"
            >
              <Search className="w-3.5 h-3.5 text-white/60" />
              <span className="hidden md:inline">Jump to tool...</span>
              <kbd className="hidden lg:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-black/30 rounded text-white/60">
                ⌘K
              </kbd>
            </button>

            {/* Role Badge */}
            <div className="hidden sm:flex items-center">
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${
                role === 'Super Admin'
                  ? 'bg-amber-400 text-ink border-amber-300'
                  : role === 'Editor'
                  ? 'bg-sky-400/20 text-sky-200 border-sky-400/40'
                  : 'bg-white/10 text-white/80 border-white/20'
              }`}>
                {role || 'Admin'}
              </span>
            </div>

            {/* Live Site Link */}
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-white p-1.5 rounded hover:bg-white/10 transition-colors"
              title="View Live Public Website"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>

            {/* Sign Out Button */}
            <button
              type="button"
              onClick={() => signOut()}
              className="text-white/60 hover:text-rose-300 p-1.5 rounded hover:bg-white/10 transition-colors"
              title={`Sign Out (${user?.email})`}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

        </div>
      </div>

      {/* Second Row: Sub-navigation tool pills for active section */}
      <div className="bg-black/25 border-t border-white/10 py-1.5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5 flex-wrap md:flex-nowrap overflow-x-auto py-0.5 max-w-full">
            <span className="text-[11px] font-bold uppercase tracking-widest text-rule-3 mr-2 shrink-0 flex items-center gap-1">
              <span>{activeSection.shortLabel}:</span>
            </span>

            {activeSection.tools.map(tool => {
              const ToolIcon = tool.icon;
              const active = isToolActive(tool);
              const isRestricted = tool.superAdminOnly && !isSuperAdmin;

              if (isRestricted) {
                return (
                  <span
                    key={tool.id}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-white/35 bg-white/5 cursor-not-allowed shrink-0 border border-transparent"
                    title="Super Admin Only"
                  >
                    <Lock className="w-3 h-3 text-amber-500/70" />
                    <span>{tool.shortLabel || tool.label}</span>
                  </span>
                );
              }

              return (
                <Link
                  key={tool.id}
                  to={tool.path}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium whitespace-nowrap shrink-0 transition-all ${
                    active
                      ? 'bg-white text-ink font-bold shadow-sm'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <ToolIcon className={`w-3.5 h-3.5 ${active ? 'text-rule-3' : 'text-white/60'}`} />
                  <span>{tool.shortLabel || tool.label}</span>
                  {tool.badge && (
                    <span className="ml-1 px-1.5 py-0.2 rounded-full text-[9px] bg-rose-500 text-white font-bold">
                      {tool.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center text-[11px] text-white/50 shrink-0">
            <span>{activeSection.description}</span>
          </div>
        </div>
      </div>

      {/* Quick Search Modal / Command Palette */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-20 px-4">
          <div className="bg-white text-ink w-full max-w-xl rounded-xl shadow-2xl border border-ground-deep overflow-hidden">
            {/* Search Input Bar */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-ground-deep bg-ground-alt/50">
              <Search className="w-5 h-5 text-ink/40 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Type to search admin tools (e.g., Blog, Applications, Quotas, SEO)..."
                className="w-full bg-transparent border-none text-ink text-sm focus:outline-none placeholder:text-ink/40"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="text-ink/40 hover:text-ink p-1 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Results List */}
            <div className="max-h-96 overflow-y-auto p-2 divide-y divide-ground-deep/40">
              {filteredTools.length === 0 ? (
                <div className="p-8 text-center text-ink/50 text-sm">
                  No admin tools found matching &quot;{searchQuery}&quot;.
                </div>
              ) : (
                filteredTools.map(tool => {
                  const ToolIcon = tool.icon;
                  const isRestricted = tool.superAdminOnly && !isSuperAdmin;

                  return (
                    <button
                      key={tool.id + tool.path}
                      type="button"
                      disabled={isRestricted}
                      onClick={() => {
                        setSearchOpen(false);
                        navigate(tool.path);
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                        isRestricted
                          ? 'opacity-50 cursor-not-allowed bg-ground-alt/30'
                          : 'hover:bg-ground-alt cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-ground-alt border border-ground-deep flex items-center justify-center shrink-0">
                          <ToolIcon className="w-4 h-4 text-rule-3" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-ink">{tool.label}</span>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-ink/50 bg-ground-alt px-1.5 py-0.5 rounded border border-ground-deep">
                              {tool.sectionLabel}
                            </span>
                            {isRestricted && (
                              <span className="text-[10px] text-amber-600 font-bold flex items-center gap-1">
                                <Lock className="w-3 h-3" /> Super Admin
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-ink/65 line-clamp-1">{tool.description}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-ink/30 shrink-0 ml-3" />
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 bg-ground-alt border-t border-ground-deep text-xs text-ink/60 flex items-center justify-between">
              <span>Press <kbd className="px-1 py-0.5 bg-white border rounded font-mono text-[10px]">Esc</kbd> to close</span>
              <span>{allTools.length} tools available across 4 sections</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPortalNav;
